/**
 * The Team page's Procore charts, drawn as `Led AI - Team Web.dc.html` draws
 * them: a weekly ink line over the pull's span, the pilot phases as flat
 * bands behind it (grey baseline, gold checkpoints, the open checkpoint a
 * shade paler), a dashed baseline-average rule labelled at the right, the
 * current partial week drawn dashed, and the latest point as a gold dot.
 *
 * Two forms. `Trend` is the card's chart, full width: a bar per bin, with
 * band labels, the rule, week ticks and a hover readout. `Sparkline` is the
 * roster's cell — a bare line on the cell's own tinted ground, no bands, no
 * labels (a 104px cell has no room for bars).
 *
 * Bins and bands come from `weeklySeries` or `dailySeries`, so a window edge
 * is always a bin edge and nothing here re-derives a date. `Trend` takes a
 * `grain`: weekly is the artboard's chart; daily draws one point per calendar
 * day, ticks each pilot-week boundary and reads the baseline rule per day.
 */

import { useEffect, useRef, useState } from "react";
import type {
  ProcoreBaselineCounts,
  ProcoreBaselineGrain,
  ProcoreBaselineWeek,
  ProcoreBaselineWindow,
} from "../../../../backend/src/shared/procoreBaseline";
import { daysBetween } from "../../../../backend/src/shared/procoreBaseline";
import { DS, FONT_MONO } from "../tokens";

export type SeriesValue = (counts: ProcoreBaselineCounts) => number;

/** The pilot windows drawn as bands — the baseline and every checkpoint the
 *  workspace's schedule defines; `pilot` spans everything and is not drawn. */
function bandKeys(weeks: ProcoreBaselineWeek[]): string[] {
  const keys = new Set<string>();
  for (const w of weeks) for (const k of w.windows) if (k === "baseline" || /^cp\d$/.test(k)) keys.add(k);
  return [...keys].sort((a, b) => (a === "baseline" ? -1 : b === "baseline" ? 1 : a.localeCompare(b, undefined, { numeric: true })));
}

function bandSpans(weeks: ProcoreBaselineWeek[]): Array<{ key: string; from: number; to: number }> {
  const spans: Array<{ key: string; from: number; to: number }> = [];
  for (const key of bandKeys(weeks)) {
    const idx = weeks.map((w, i) => (w.windows.includes(key) ? i : -1)).filter((i) => i >= 0);
    if (idx.length) spans.push({ key, from: idx[0]!, to: idx[idx.length - 1]! });
  }
  return spans;
}

function xAt(i: number, n: number, width: number, inset: number): number {
  if (n <= 1) return width / 2;
  return inset + (i * (width - inset * 2)) / (n - 1);
}

function pathOf(points: Array<[number, number]>): string {
  return points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
}

// ---------------------------------------------------------------------------
// Row form
// ---------------------------------------------------------------------------

export function Sparkline({
  weeks,
  value,
  width = 104,
  height = 34,
  stroke = DS.ink,
  testId,
}: {
  weeks: ProcoreBaselineWeek[];
  value: SeriesValue;
  /** Unused by the row form; kept so callers can pass the same props to both forms. */
  hasPilot?: boolean;
  width?: number;
  height?: number;
  stroke?: string;
  testId?: string;
}) {
  // Future bins have no data to draw; the row form has no bands to need them.
  weeks = weeks.filter((w) => !w.future);
  const values = weeks.map((w) => value(w.counts));
  const max = Math.max(1, ...values);
  const inset = 6;
  const top = 5;
  const bottom = height - 5;
  const y = (v: number) => bottom - (v / max) * (bottom - top);
  const n = weeks.length;
  const points: Array<[number, number]> = values.map((v, i) => [xAt(i, n, width, inset), y(v)]);
  const lastPartial = n > 1 && !!weeks[n - 1]?.partial;
  const solid = lastPartial ? points.slice(0, -1) : points;
  const tail = lastPartial ? points.slice(-2) : [];
  return (
    <svg data-testid={testId} width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`Weekly trend, ${n} weeks`} style={{ display: "block", overflow: "visible" }}>
      {solid.length > 1 && <path d={pathOf(solid)} fill="none" stroke={stroke} strokeWidth={1.6} strokeLinejoin="round" strokeLinecap="round" />}
      {tail.length === 2 && <path d={pathOf(tail)} fill="none" stroke={stroke} strokeWidth={1.6} strokeDasharray="3 3" strokeLinecap="round" />}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Card form
// ---------------------------------------------------------------------------

function useWidth<T extends HTMLElement>(fallback: number): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setWidth(w);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

const bandLabel = (key: string) => (key === "baseline" ? "BASELINE" : key.toUpperCase());

function shortDate(ymd: string): string {
  const [, m, d] = ymd.split("-");
  return `${Number(m)}/${Number(d)}`;
}

function weekday(ymd: string): string {
  return new Date(`${ymd}T00:00:00Z`).toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" });
}

/** The hover readout's heading: a day's weekday and date, or a week's range. */
function binLabel(bin: ProcoreBaselineWeek): string {
  return bin.start === bin.end ? `${weekday(bin.start)} ${shortDate(bin.start)}` : `${shortDate(bin.start)} – ${shortDate(bin.end)}`;
}

/** One of a stacked bar's series: what it counts, its colour, and its name
 *  in the hover readout. */
export interface TrendSeries {
  key: string;
  label: string;
  color: string;
  value: SeriesValue;
}

/** A nice gridline step for `v`: 1, 2 or 5 × a power of ten, at or above it,
 *  and never under 1 — the counts are whole numbers. */
function niceStep(v: number): number {
  if (v <= 1) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  const m = v / p;
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * p;
}

export function Trend({
  weeks: allWeeks,
  value,
  windows,
  unit,
  grain = "week",
  height = 210,
  testId,
  stack,
  barColor,
  seriesLabel,
  focus,
  onSelect,
}: {
  /** The series' bins — weeks or days, per `grain`. */
  weeks: ProcoreBaselineWeek[];
  value: SeriesValue;
  hasPilot?: boolean;
  windows: ProcoreBaselineWindow[];
  /** What each bin is. Names the axis, the rule's unit and the tick cadence. */
  grain?: ProcoreBaselineGrain;
  /** The noun under the hover number — "records", "daily logs". */
  unit: string;
  height?: number;
  testId?: string;
  /** Split each finished bar into these, bottom to top, in proportion to
   *  what each counts of the bin (`Led AI - Records vs Photos Chart.dc.html`:
   *  records stack by type). The current bin stays one gold bar. The hover
   *  readout lists exactly these, the one under the pointer in bold. */
  stack?: TrendSeries[];
  /** The finished bars' ink when there is no stack. */
  barColor?: string;
  /** The single series' name in the hover readout when there is no stack. */
  seriesLabel?: string;
  /** Zoom the x axis to the bins overlapping this range. Set by a drag on the
   *  chart; the baseline rule and the current bin still read the whole series. */
  focus?: { start: string; end: string } | null;
  /** A drag across bins ended here: its first bin's start and last bin's end. */
  onSelect?: (range: { start: string; end: string }) => void;
}) {
  const [ref, width] = useWidth<HTMLDivElement>(640);
  /** The bar under the pointer, and which of its segments (null: the bar as a whole). */
  const [hover, setHover] = useState<{ bin: number; seg: number | null } | null>(null);
  const [drag, setDrag] = useState<{ from: number; to: number; moved: boolean } | null>(null);
  // The x axis spans every bin — the whole pilot, checkpoints to come
  // included — or, zoomed, only the bins of the focused range; a point is
  // drawn only for bins that have begun.
  const currentStart = allWeeks.filter((w) => !w.future).at(-1)?.start;
  const weeks = focus ? allWeeks.filter((w) => w.end >= focus.start && w.start <= focus.end) : allWeeks;
  const n = weeks.length;
  const drawn = weeks.filter((w) => !w.future).length;
  const values = weeks.slice(0, drawn).map((w) => value(w.counts));
  // A standard axis: about four whole-number steps, gridlines labelled at the left.
  const peak = Math.max(1, ...values) * 1.05;
  const step = niceStep(peak / 4);
  const axisTop = Math.ceil(peak / step) * step;
  const max = axisTop;
  const gutter = 30; // the y labels
  const inset = 6;
  const labelBand = 20; // band labels above the plot
  const axis = 24; // week ticks below
  const top = labelBand;
  const bottom = height - axis;
  const y = (v: number) => bottom - (v / max) * (bottom - top);
  const yTicks = Array.from({ length: axisTop / step + 1 }, (_, k) => k * step);
  // One slot per bin across the plot; a bar fills its slot less a 2px gap
  // either side, so adjacent bars never touch. Bands snap to slot edges.
  const plotLeft = gutter + inset;
  const slot = n > 0 ? (width - plotLeft - inset) / n : width;
  const slotX = (i: number) => plotLeft + i * slot;
  const centerX = (i: number) => slotX(i) + slot / 2;
  const gap = slot >= 8 ? 2 : slot >= 4 ? 1 : 0.5;
  // Zoomed to a few bins, a slot is wide: the bar keeps to a column's width, centred.
  const barW = Math.min(72, Math.max(1, slot - gap * 2));
  // A finished week bin shorter than seven days — the pilot's first, when it
  // starts mid-week — is drawn as narrow as its share of a week, so a 4-day
  // count does not pass for a week's.
  const binDays = (w: ProcoreBaselineWeek) => daysBetween(w.start, w.end) + 1;
  const short = (w: ProcoreBaselineWeek | undefined) => grain === "week" && !!w && !w.partial && binDays(w) < 7;
  const bars = values.map((v, i) => {
    const w = short(weeks[i]) ? Math.max(1, (barW * binDays(weeks[i]!)) / 7) : barW;
    return { x: centerX(i) - w / 2, w, top: y(v), v };
  });
  const bands = bandSpans(weeks);
  const bandX = (i: number, edge: "from" | "to") => (edge === "from" ? slotX(i) : slotX(i) + slot);
  const binAt = (px: number) => Math.max(0, Math.min(n - 1, Math.floor((px - plotLeft) / slot)));

  // A finished bar's segments, bottom to top, each as tall as its share.
  const segmentsOf = (i: number): Array<{ color: string; label: string; v: number; y: number; h: number }> | null => {
    const b = bars[i];
    if (!b || !stack || weeks[i]!.start === currentStart || b.v <= 0) return null;
    const parts = stack.map((s) => ({ color: s.color, label: s.label, v: Math.max(0, s.value(weeks[i]!.counts)) }));
    const total = parts.reduce((t, q) => t + q.v, 0);
    if (total <= 0) return null;
    let yTop = bottom;
    return parts.map((q) => {
      const h = ((bottom - b.top) * q.v) / total;
      yTop -= h;
      return { ...q, y: yTop, h };
    });
  };

  const baselineBins = allWeeks.filter((w) => w.windows.includes("baseline") && !w.partial);
  const baselineAvg = baselineBins.length ? baselineBins.reduce((s, w) => s + value(w.counts), 0) / baselineBins.length : null;
  // The rule's label sits at the right, as the artboard draws it — unless
  // bars stand in that corner. Then it moves to wherever along the rule the
  // bars are shortest, so it prints over as little data as possible.
  const labelX = (() => {
    if (baselineAvg === null) return { x: width - 4, anchor: "end" as const };
    const ry = y(baselineAvg);
    const barTop = (x: number): number | null => {
      const i = Math.floor((x - plotLeft) / slot);
      const b = bars[i];
      if (!b || x < b.x || x > b.x + b.w) return null;
      return b.v > 0 ? b.top : null;
    };
    const LABEL_W = 96;
    const BAND_TOP = ry - 14;
    const BAND_BOTTOM = ry - 2;
    const CAP = 40;
    const clearanceAt = (x: number): number => {
      const t = barTop(x);
      if (t === null) return CAP;
      if (t > BAND_BOTTOM) return Math.min(CAP, t - BAND_BOTTOM);
      return 0;
    };
    const worst = (from: number, to: number) => {
      let min = CAP;
      for (let x = from; x <= to; x += 2) min = Math.min(min, clearanceAt(x));
      return min;
    };
    if (worst(width - 4 - LABEL_W, width - 4) >= 3) return { x: width - 4, anchor: "end" as const };
    const average = (from: number, to: number) => {
      let sum = 0;
      let k = 0;
      for (let x = from; x <= to; x += 2, k++) sum += clearanceAt(x);
      return k ? sum / k : 0;
    };
    let best = { x: width - 4, c: -1 };
    for (let x = plotLeft + LABEL_W / 2; x <= width - inset - LABEL_W / 2; x += 8) {
      const c = average(x - LABEL_W / 2, x + LABEL_W / 2);
      if (c > best.c) best = { x, c };
    }
    return { x: best.x, anchor: "middle" as const };
  })();

  const perUnit = grain === "day" ? "DAY" : "WK";
  const hovered = hover ? weeks[hover.bin] : undefined;
  // Hover is per SEGMENT: the pointer must be on a bar, and on a stacked bar
  // the readout bolds the segment it is on. Empty column: no readout.
  const locate = (e: React.PointerEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    if (drawn === 0 || px < plotLeft) return null;
    const bin = binAt(px);
    const b = bars[bin];
    if (!b || b.v <= 0 || px < b.x || px > b.x + b.w || py < b.top || py > bottom) return { bin, seg: null, onBar: false };
    const segs = segmentsOf(bin);
    const seg = segs ? segs.findIndex((sg) => py >= sg.y && py <= sg.y + sg.h) : -1;
    return { bin, seg: seg >= 0 ? seg : null, onBar: true };
  };
  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const at = locate(e);
    if (drag) {
      if (at) setDrag({ ...drag, to: at.bin, moved: drag.moved || at.bin !== drag.from });
      setHover(null);
      return;
    }
    setHover(at && at.onBar ? { bin: at.bin, seg: at.seg } : null);
  };
  const onDown = (e: React.PointerEvent<SVGSVGElement>) => {
    const at = locate(e);
    if (!at || !onSelect) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDrag({ from: at.bin, to: at.bin, moved: false });
  };
  const onUp = () => {
    if (drag && drag.moved && onSelect) {
      const a = Math.min(drag.from, drag.to);
      const b = Math.max(drag.from, drag.to);
      onSelect({ start: weeks[a]!.start, end: weeks[b]!.end });
    }
    setDrag(null);
  };
  const dragSpan = drag && drag.moved ? { a: Math.min(drag.from, drag.to), b: Math.max(drag.from, drag.to) } : null;

  const maxTicks = Math.max(2, Math.floor(width / 56));
  const dayStep = 7 * Math.max(1, Math.ceil(n / 7 / maxTicks));
  const tickX = (i: number) => centerX(i);
  const ticks = (() => {
    // Zoomed, every bin is a candidate; the spacing rule below thins them.
    const chosen: boolean[] = weeks.map((w, i) => (focus ? true : grain === "day" ? w.index % dayStep === 0 : i % 2 === 0));
    let lastX = -Infinity;
    const out = chosen.map((on, i) => {
      if (!on || i === n - 1) return false;
      // Zoomed, every label is centred, so neighbours need only their own width apart.
      if (tickX(i) - lastX < 34 || tickX(n - 1) - tickX(i) < (focus ? 34 : 52)) return false;
      lastX = tickX(i);
      return true;
    });
    if (n > 0) out[n - 1] = true;
    return out;
  })();
  const showTick = (i: number) => ticks[i] ?? false;
  const windowFor = (key: string) => windows.find((w) => w.key === key);
  const barPath = (b: { x: number; w: number; top: number }) => {
    const r = Math.min(4, b.w / 2, Math.max(0, bottom - b.top));
    const x1 = b.x + b.w;
    return `M${b.x.toFixed(1)},${bottom} V${(b.top + r).toFixed(1)} Q${b.x.toFixed(1)},${b.top.toFixed(1)} ${(b.x + r).toFixed(1)},${b.top.toFixed(1)} H${(x1 - r).toFixed(1)} Q${x1.toFixed(1)},${b.top.toFixed(1)} ${x1.toFixed(1)},${(b.top + r).toFixed(1)} V${bottom} Z`;
  };

  // The readout's rows: the stack's series for a stacked bar, the one series otherwise.
  const rows = hovered && hover
    ? stack
      ? stack.map((s, k) => ({ label: s.label, color: s.color, v: s.value(hovered.counts), bold: hover.seg === k }))
      : [{ label: seriesLabel ?? unit, color: barColor ?? DS.ink, v: value(hovered.counts), bold: true }]
    : [];
  const tipW = 190;
  const hoveredShort = short(hovered);
  const tipH = 58 + rows.length * 19 + (hoveredShort ? 18 : 0);
  const tipLeft = hover ? (centerX(hover.bin) + slot / 2 + 10 + tipW <= width ? centerX(hover.bin) + slot / 2 + 10 : Math.max(0, centerX(hover.bin) - slot / 2 - 10 - tipW)) : 0;
  const tipAnchorY = hover ? (() => { const segs = segmentsOf(hover.bin); const sg = hover.seg !== null && segs ? segs[hover.seg] : null; return sg ? sg.y + sg.h / 2 : bars[hover.bin]!.top + 12; })() : 0;
  const tipTop = Math.max(top, Math.min(height - tipH, tipAnchorY - tipH / 2));

  return (
    <div ref={ref} data-testid={testId} style={{ position: "relative", width: "100%" }}>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`${unit} per ${grain}`}
        data-grain={grain}
        data-bins={n}
        style={{ display: "block", overflow: "visible", cursor: drag ? "col-resize" : hover ? "pointer" : "crosshair", userSelect: "none", touchAction: "none" }}
        onPointerMove={onMove}
        onPointerDown={onDown}
        onPointerUp={onUp}
        onPointerLeave={() => { setHover(null); if (!drag) return; onUp(); }}
      >
        {/* The y axis: gridlines, labelled at the left. */}
        {yTicks.map((v) => (
          <g key={v} data-testid="trend-ytick">
            <line x1={gutter} x2={width} y1={y(v)} y2={y(v)} stroke={DS.ink} strokeOpacity={v === 0 ? 0.18 : 0.07} strokeWidth={1} />
            <text x={gutter - 6} y={y(v) + 3} textAnchor="end" fontFamily={FONT_MONO} fontSize={9} fontWeight={500} fill={DS.ink3}>
              {v}
            </text>
          </g>
        ))}
        {bands.map((b) => {
          const x0 = Math.max(0, bandX(b.from, "from")) + 2;
          const x1 = Math.min(width, bandX(b.to, "to")) - 2;
          const w = windowFor(b.key);
          const open = !!w?.partial;
          const ahead = !!w?.future;
          const fill = b.key === "baseline" ? DS.bandBase : open || ahead ? DS.bandCpOpen : DS.bandCp;
          const ink = b.key === "baseline" ? DS.meta : DS.bandCpInk;
          const isLast = b === bands[bands.length - 1];
          const fits = (chars: number) => chars * 7.2 <= Math.max(0, x1 - x0);
          const full = `${bandLabel(b.key)}${open ? " · SO FAR" : ""}`;
          const label = fits(full.length) ? full : fits(bandLabel(b.key).length) ? bandLabel(b.key) : "";
          return (
            <g key={b.key}>
              <rect x={x0} y={top} width={Math.max(0, x1 - x0)} height={bottom - top} fill={fill} fillOpacity={ahead ? 0.55 : 0.85} rx={8} />
              {label && (
                <text x={isLast ? x1 : (x0 + x1) / 2} y={labelBand - 7} textAnchor={isLast ? "end" : "middle"} fontFamily={FONT_MONO} fontSize={9} fontWeight={700} letterSpacing="1.2" fill={ink}>
                  {label}
                </text>
              )}
            </g>
          );
        })}
        {/* The bars: ink (or stacked by type), the current bin gold (it is still
            filling); the hovered segment ringed. */}
        {bars.map((b, i) => {
          if (b.v <= 0) return null;
          const w = weeks[i]!;
          const current = w.start === currentStart;
          const segments = segmentsOf(i);
          const clipId = `${testId ?? "trend"}-clip-${i}`;
          const on = hover?.bin === i;
          return (
            <g key={w.start}>
              {segments && (
                <>
                  <clipPath id={clipId}>
                    <path d={barPath(b)} />
                  </clipPath>
                  <g clipPath={`url(#${clipId})`}>
                    {segments.map((sg, k) => (
                      <rect key={k} data-testid="trend-segment" data-series={stack![k]!.key} x={b.x} y={sg.y} width={b.w} height={sg.h} fill={sg.color} opacity={on && hover?.seg !== null && hover?.seg !== k ? 0.55 : 1} />
                    ))}
                    {segments.slice(0, -1).map((sg, k) => (
                      <line key={k} x1={b.x} x2={b.x + b.w} y1={sg.y} y2={sg.y} stroke={DS.surface} strokeWidth={2} />
                    ))}
                    {on && hover?.seg !== null && segments[hover!.seg!] && (
                      <rect x={b.x + 0.75} y={segments[hover!.seg!]!.y + 0.75} width={b.w - 1.5} height={Math.max(0, segments[hover!.seg!]!.h - 1.5)} fill="none" stroke={DS.ink} strokeWidth={1.5} />
                    )}
                  </g>
                </>
              )}
              <path
                data-testid="trend-bar"
                data-bin={w.start}
                data-partial={w.partial ? "true" : undefined}
                d={barPath(b)}
                fill={current ? DS.gold : segments ? "none" : (barColor ?? DS.ink)}
                stroke={on && (!segments || hover?.seg === null) ? DS.ink : "none"}
                strokeWidth={on && (!segments || hover?.seg === null) ? 1.5 : 0}
              />
            </g>
          );
        })}
        {/* The rule and its label print over the bars, as the artboard draws
            them; the label's halo keeps it legible where a bar stands. */}
        {baselineAvg !== null && baselineAvg > 0 && (
          <g>
            <line x1={gutter} x2={width} y1={y(baselineAvg)} y2={y(baselineAvg)} stroke={DS.ink} strokeOpacity={0.28} strokeWidth={1} strokeDasharray="3 3" />
            <text x={labelX.x} y={y(baselineAvg) - 5} textAnchor={labelX.anchor} fontFamily={FONT_MONO} fontSize={9} fontWeight={600} letterSpacing="0.8" fill={DS.meta} style={{ paintOrder: "stroke" }} stroke={DS.surface} strokeWidth={5}>
              BASELINE {baselineAvg >= 10 ? Math.round(baselineAvg) : Math.round(baselineAvg * 10) / 10}/{perUnit}
            </text>
          </g>
        )}
        {/* The drag in progress, and the selection it made. */}
        {dragSpan && <rect data-testid="trend-drag" x={slotX(dragSpan.a)} y={top} width={slotX(dragSpan.b) + slot - slotX(dragSpan.a)} height={bottom - top} fill={DS.ink} fillOpacity={0.06} stroke={DS.ink} strokeOpacity={0.35} strokeWidth={1} rx={6} />}
        {weeks.map((w, i) =>
          showTick(i) ? (
            <text key={w.start} x={tickX(i)} y={height - 6} textAnchor={focus ? "middle" : i === 0 ? "start" : i === n - 1 ? "end" : "middle"} fontFamily={FONT_MONO} fontSize={9.5} fontWeight={500} fill={DS.ink3}>
              {shortDate(w.start)}
            </text>
          ) : null,
        )}
      </svg>
      {hovered && hover && !drag && (
        <div
          role="tooltip"
          data-testid="trend-tooltip"
          style={{ position: "absolute", left: tipLeft, top: tipTop, width: tipW, padding: "8px 10px", borderRadius: 10, background: DS.surface, border: `1px solid ${DS.line2}`, boxShadow: "0 6px 20px rgba(10,10,10,0.12)", pointerEvents: "none", fontSize: 12.5, color: DS.ink, lineHeight: 1.45 }}
        >
          <div style={{ fontFamily: FONT_MONO, fontSize: 9, fontWeight: 600, letterSpacing: "1px", color: DS.meta, textTransform: "uppercase" }}>
            {binLabel(hovered)}
            {hovered.partial ? " · so far" : hoveredShort ? ` · ${binDays(hovered)} days` : ""}
          </div>
          <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: "-0.4px" }}>
            {value(hovered.counts)} <span style={{ fontSize: 12, fontWeight: 600, color: DS.ink3 }}>{unit}</span>
          </div>
          {hoveredShort && (
            <div data-testid="trend-tooltip-pace" style={{ fontSize: 12, color: DS.ink3, marginBottom: 2 }}>
              A {binDays(hovered)}-day week — {Math.round((value(hovered.counts) / binDays(hovered)) * 7)}/wk pace
            </div>
          )}
          {rows.map((r) => (
            <div key={r.label} data-testid="trend-tooltip-row" data-bold={r.bold ? "1" : "0"} style={{ display: "flex", alignItems: "center", gap: 7, color: r.bold ? DS.ink : DS.body2, fontWeight: r.bold ? 800 : 500 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: r.color, display: "block", flexShrink: 0 }} />
              <span style={{ flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.label}</span>
              <span style={{ fontVariantNumeric: "tabular-nums", color: r.bold ? DS.ink : DS.ink }}>{r.v}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
